<template>
    <div class="main" v-if="HasPermission(PermissionList.Marketing_Finance)">
      <h3>Cost</h3>
      <!-- Add Cost Button -->
      <b-row class="align-items-center mb-4">
        <b-col>
          <div class="d-flex justify-content-end align-items-center">
            <b-button v-if="!$route.fullPath.includes('view')" variant="primary" v-b-modal.modal-add-cost>
              Add Cost
            </b-button>
          </div>
        </b-col>
      </b-row>
  
      <!-- Search Input and Total Count -->
      <b-row class="mb-4 align-items-center">
        <b-col md="4">
          <b-form-input v-model="filter" placeholder="Search costs..." />
        </b-col>
        <b-col md="8" class="text-right">
          <span class="text-muted">
            Showing {{ filteredCosts.length }} of {{ totalCount }} costs
          </span>
        </b-col>
      </b-row>
  
      <!-- No Costs Available Message -->
      <div v-if="filteredCosts.length === 0 && !loading">
        <b-row class="justify-content-center">
          <b-col md="4">
            <b-card>
              <b-card-body class="text-center">
                <h2>No costs available</h2>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </div>
  
      <!-- Costs Table -->
      <div v-else>
        <div v-if="loading" class="text-center my-4">
          <b-spinner label="Loading costs..."></b-spinner>
        </div>
  
        <b-table
          v-else
          striped
          hover
          responsive
          :items="paginatedCosts"
          :fields="fields"
          :filter="filter"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          @filtered="onFiltered"
        >
  <template #cell(contentName)="data">
    {{ data.item.contentName ? data.item.contentName : "No Content" }}
  </template>
          <template #cell(actions)="data">
            <b-dropdown v-if="!$route.fullPath.includes('view')" id="actions-dropdown" variant="link" size="sm"
              text="Actions" right no-caret>
              <template #button-content>
                <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
              </template>
              <b-dropdown-item @click="editCost(data.item)">
                <feather-icon icon="EditIcon" size="16" class="mr-50" />
                Edit
              </b-dropdown-item>
              <b-dropdown-item @click="deleteCost(data.item.costId)">
                <feather-icon icon="TrashIcon" size="16" class="mr-50" />
                Delete
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
  
        <!-- Pagination -->
        <b-pagination
          v-if="!loading"
          v-model="currentPage"
          :per-page="perPage"
          :total-rows="filteredCosts.length"
          align="center"
          class="my-2"
        />
      </div>
  
      <!-- Add/Edit Cost Modal -->
      <b-modal id="modal-add-cost" :title="isEditing ? 'Edit Cost' : 'Add Cost'" hide-footer size="lg" centered
        @hidden="resetForm">
        <b-form @submit.prevent="validateAndSubmit">
          <b-row>
            <!-- Category -->
            <b-col md="6">
              <b-form-group label="Name" label-for="cost_category">
                <b-form-input id="cost_category" placeholder="Name" v-model="newCost.category"
                  :class="{ 'is-invalid': !isCategoryValid }" @blur="validateCategory" />
                <div class="invalid-feedback" v-if="!isCategoryValid">Name is required</div>
              </b-form-group>
            </b-col>
  
            <!-- Description -->
            <b-col md="6">
              <b-form-group label="Description" label-for="cost_description">
                <b-form-input id="cost_description" placeholder="Description" v-model="newCost.description"
                  :class="{ 'is-invalid': !isDescriptionValid }" @blur="validateDescription" />
                <div class="invalid-feedback" v-if="!isDescriptionValid">Description is required</div>
              </b-form-group>
            </b-col>
  
            <!-- Amount -->
            <b-col md="6">
              <b-form-group label="Amount" label-for="cost_amount">
                <b-form-input type="number" id="cost_amount" placeholder="Amount" v-model="newCost.amount"
                  :class="{ 'is-invalid': !isAmountValid }" @blur="validateAmount" />
                <div class="invalid-feedback" v-if="!isAmountValid">Amount must be a positive number</div>
              </b-form-group>
            </b-col>
  
            <!-- Date -->
            <b-col md="6">
              <b-form-group label="Date" label-for="cost_date">
                <b-form-datepicker id="cost_date" :min="currentDate" v-model="newCost.date"
                  :class="{ 'is-invalid': !isDateValid }" @input="validateDate" />
                <div class="invalid-feedback" v-if="!isDateValid">Date is required</div>
              </b-form-group>
            </b-col>
  
            <!-- Submit Button -->
            <b-col md="12" class="d-flex justify-content-end">
              <b-button type="submit" variant="primary">{{ isEditing ? 'Update Cost' : 'Add Cost' }}</b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-modal>
    </div>
  </template>
  
  <script>
  import Swal from "sweetalert2";
  import { mapActions, mapGetters } from "vuex";
  import { PermissionList } from "@/libs/defaultList";
  import { HasPermission } from "@/@core/utils/validations/permissionCheck";
  import {
    BRow,
    BCol,
    BButton,
    BCard,
    BCardBody,
    BTable,
    BDropdown,
    BDropdownItem,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormDatepicker,
    BPagination,
    BSpinner,
  } from "bootstrap-vue";
  
  export default {
    components: {
      BRow,
      BCol,
      BButton,
      BCard,
      BCardBody,
      BTable,
      BDropdown,
      BDropdownItem,
      BModal,
      BForm,
      BFormGroup,
      BFormInput,
      BFormDatepicker,
      BPagination,
      BSpinner,
    },
    data() {
      return {
        campaignId: this.$route.params.id,
        cost: [],
        currentPage: 1,
        perPage: 5,
        filter: "",
        sortBy: "category",
        sortDesc: false,
        fields: [
          { key: "category", label: "Name", sortable: true },
          { key: "description", label: "Description", sortable: true },
          { key: "amount", label: "Amount", sortable: true },
          { key: "date", label: "Date", sortable: true, formatter: (value) => new Date(value).toLocaleDateString() },
          {
  key: "contentName",
  label: "Related Content",
  sortable: true,
  formatter: (value) => value ? value : "No Content"
},

          { key: "actions", label: "Actions" },
        ],
        newCost: {
          campaignId: this.$route.params.id,
          category: "",
          description: "",
          amount: null,
          date: "",
        },
        isCategoryValid: true,
        isDescriptionValid: true,
        isAmountValid: true,
        isDateValid: true,
        currentDate: new Date().toISOString().split("T")[0],
        isEditing: false,
        editingCostId: null,
        loading: true,
      };
    },

    setup(props) {
    return {
      PermissionList,
      HasPermission,
    };
  },
    computed: {
      ...mapGetters("marketingModule", ["cost", "loading"]),
      // Total count of all costs (unfiltered)
      totalCount() {
        return this.cost.length;
      },
      // Filtered costs based on search input
      filteredCosts() {
        return this.cost.filter((cost) => {
          return (
            cost.category.toLowerCase().includes(this.filter.toLowerCase()) ||
            cost.description.toLowerCase().includes(this.filter.toLowerCase()) ||
            cost.amount.toString().includes(this.filter) ||
            new Date(cost.date).toLocaleDateString().includes(this.filter)
          );
        });
      },
      // Paginated costs
      paginatedCosts() {
        const start = (this.currentPage - 1) * this.perPage;
        const end = start + this.perPage;
        return this.filteredCosts.slice(start, end);
      },
    },
    methods: {
      ...mapActions("marketingModule", [
        "getCostListAction",
        "postCostAction",
        "putCostAction",
        "deleteCostAction",
      ]),
      async fetchCosts() {
        this.loading = true;
        try {
          const response = await this.getCostListAction(this.campaignId);
          if (response && response.data && Array.isArray(response.data)) {
            this.cost = response.data;
          } else {
            console.error("Unexpected API response structure:", response);
            this.cost = [];
          }
        } catch (error) {
          console.error("Error fetching costs:", error);
          Swal.fire("Error", "Failed to fetch costs.", "error");
        } finally {
          this.loading = false;
        }
      },
      async addCost() {
        let loader = this.$swal({
                title: "Processing...",
                text: "Please wait while the cost is being added",
                onOpen: () => {
                    this.$swal.showLoading();
                },
                allowOutsideClick: () => !this.$swal.isLoading(),
                buttonsStyling: false,
                showConfirmButton: false,
            });
        const payload = {
          campaignId: this.campaignId,
          category: this.newCost.category,
          description: this.newCost.description,
          amount: this.newCost.amount,
          date: this.newCost.date,
        };
  
        try {
          await this.postCostAction(payload);
          await this.fetchCosts();
          this.$bvModal.hide("modal-add-cost");
          Swal.fire("Success", "Cost added successfully.", "success");
        } catch (error) {
          console.error("Error adding cost:", error);
          Swal.fire("Error", "Failed to add cost.", "error");
        }
      },
      async updateCost() {
        let loader = this.$swal({
                title: "Processing...",
                text: "Please wait while the cost is being updated",
                onOpen: () => {
                    this.$swal.showLoading();
                },
                allowOutsideClick: () => !this.$swal.isLoading(),
                buttonsStyling: false,
                showConfirmButton: false,
            });
        const payload = {
          campaignId: this.campaignId,
          category: this.newCost.category,
          description: this.newCost.description,
          amount: this.newCost.amount,
          date: this.newCost.date,
          costId: this.editingCostId,
        };
  
        try {
          await this.putCostAction(payload);
          await this.fetchCosts();
          this.$bvModal.hide("modal-add-cost");
          Swal.fire("Success", "Cost updated successfully.", "success");
        } catch (error) {
          console.error("Error updating cost:", error);
          Swal.fire("Error", "Failed to update cost.", "error");
        }
      },
      async deleteCost(costId) {
        const result = await Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, delete it!",
              cancelButtonText: "Cancel",
              didOpen: () => {
                  document.querySelector(".swal2-actions").style.display = "flex";
                  document.querySelector(".swal2-actions").style.justifyContent = "center";
                  document.querySelector(".swal2-actions").style.gap = "20px";
              },
        });
        if (result.isConfirmed) {
          try {
            let loader = this.$swal({
                title: "Processing...",
                text: "Please wait while the Deleting Cost",
                onOpen: () => {
                    this.$swal.showLoading();
                },
                allowOutsideClick: () => !this.$swal.isLoading(),
                buttonsStyling: false,
                showConfirmButton: false,
            });
            await this.deleteCostAction(costId);
            await this.fetchCosts();
            Swal.fire("Deleted!", "Cost has been deleted.", "success");
          } catch (error) {
            console.error("Error deleting cost:", error);
            Swal.fire("Error", "Failed to delete cost.", "error");
          }
        }
      },
      editCost(cost) {
        this.newCost = { ...cost };
        this.editingCostId = cost.costId;
        this.isEditing = true;
        this.$bvModal.show("modal-add-cost");
      },
      resetForm() {
        this.newCost = {
          campaignId: this.campaignId,
          category: "",
          description: "",
          amount: null,
          date: "",
        };
        this.isEditing = false;
        this.editingCostId = null;
      },
      validateAndSubmit() {
        this.validateCategory();
        this.validateDescription();
        this.validateAmount();
        this.validateDate();
  
        if (this.isCategoryValid && this.isDescriptionValid && this.isAmountValid && this.isDateValid) {
          this.isEditing ? this.updateCost() : this.addCost();
        } else {
          Swal.fire("Validation Error", "Please fill in all required fields.", "error");
        }
      },
      validateCategory() {
        this.isCategoryValid = this.newCost.category.trim() !== "" ? true : false;
      },
      validateDescription() {
        this.isDescriptionValid = this.newCost.description.trim() !== "" ? true : false;
      },
      validateAmount() {
        this.isAmountValid = this.newCost.amount > 0 ? true : false;
      },
      validateDate() {
        this.isDateValid = this.newCost.date ? true : false;
      },
      onFiltered(filteredItems) {
        this.currentPage = 1; // Reset pagination when filtering
      },
    },
    async mounted() {
      await this.fetchCosts();
    },
  };
  </script>
  
  <style scoped>
  .main {
    padding: 20px;
  }
  .swal-actions-spacing {
    display: flex;
    justify-content: center;
    /* Align buttons in the center */
    gap: 15px;
    /* Adjust space between buttons */
  }
  
  /* Optional: Adjust individual button margins */
  .swal-confirm-btn {
    padding: 8px 16px;
  }
  
  .swal-cancel-btn {
    padding: 8px 16px;
  }
  </style>